import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/legacy-i18n';
import { currentLocale } from '@trello/locale';
import { Button } from '@trello/nachos/button';
import { WarningIcon } from '@trello/nachos/icons/warning';
import { Spinner } from '@trello/nachos/spinner';
import { navigate } from '@trello/router/navigate';
import { token } from '@trello/theme';

import { Alerts } from 'app/scripts/views/lib/Alerts';
import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { useDeletePluginMutation } from './DeletePluginMutation.generated';
import { usePluginDeleteQuery } from './PluginDeleteQuery.generated';

import styles from './PowerUpDeletePage.less';

const format = forTemplate('app_management');

export const PowerUpDeletePage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const { data, error, loading } = usePluginDeleteQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });

  const [deletePlugin, { loading: deleting }] = useDeletePluginMutation();

  const [confirmationName, setConfirmationName] = useState('');
  const plugin = data?.plugin;

  const onDelete = useCallback(async () => {
    const traceId = Analytics.startTask({
      taskName: 'edit-plugin/deletePowerUp',
      source: 'powerUpAdminDeleteScreen',
      attributes: {
        idPlugin,
      },
    });
    try {
      await deletePlugin({
        variables: {
          idPlugin,
        },
      });
      Analytics.taskSucceeded({
        taskName: 'edit-plugin/deletePowerUp',
        traceId,
        source: 'powerUpAdminDeleteScreen',
        attributes: {
          idPlugin,
        },
      });
      Alerts.show('power-up-deleted', 'info', 'powerupdeleted', 3000);
      navigate('/power-ups/admin', { trigger: true });
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'edit-plugin/deletePowerUp',
        traceId,
        source: 'powerUpAdminDeleteScreen',
        attributes: {
          idPlugin,
        },
        error,
      });
    }
  }, [deletePlugin, idPlugin]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !plugin) {
    return <div>{format('something-went-wrong')}</div>;
  }

  let name = plugin.name || plugin.id;

  if (plugin.listings?.length) {
    let listing = plugin.listings.find((l) => l.locale === currentLocale);
    if (!listing) {
      listing = plugin.listings[0];
    }
    name = listing.name;
  }

  const confirmed = confirmationName === name;

  let toDeleteContent;

  if (plugin.public) {
    toDeleteContent = (
      <>
        <p>{format('public-delete-warning')}</p>
        <p>{format('please-contact-us-to-delete')}</p>
      </>
    );
  } else {
    toDeleteContent = (
      <>
        <p
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: format('to-delete-this-power-up', {
              pluginName: name,
            }),
          }}
        />
        <div className={styles.pluginInput}>
          <label>
            {format('power-up-name')}
            <input
              type="text"
              className={styles.pluginInput}
              name="name"
              placeholder={format('enter-the-name-of-this-power-up')}
              value={confirmationName}
              onChange={(e) => setConfirmationName(e.target.value)}
              disabled={deleting}
            />
          </label>
        </div>
        <div
          className={classNames(
            styles.footer,
            styles.formFooter,
            styles.deleteFooter,
          )}
        >
          <WarningIcon
            size="large"
            color={token('color.icon.accent.red', '#C9372C')}
          />
          <span className={styles.deleteWarning}>
            <b>{format('delete-warning')}</b>
          </span>
          <Button
            appearance="danger"
            onClick={onDelete}
            isDisabled={!confirmed || deleting}
          >
            {deleting ? <Spinner small /> : format('delete')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <h1>{format('delete-power-up')}</h1>
      </div>

      <p>
        {format('your-power-up-is-enabled-n-boards', {
          boardCount: plugin.usageBrackets.boards,
        })}
      </p>

      {toDeleteContent}
    </>
  );
};
